import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params } from '@angular/router';

import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { LiveUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { toBoolean } from '@frontend/vanilla/core';
import { findLast } from 'lodash-es';
import { Observable, map, of } from 'rxjs';

import { LiveEventListService } from './live-event-list.service';
import { LiveHistoryService, RouteHistoryData } from './live-history.service';

type ActivationCheck = (routeData: RouteHistoryData) => Observable<boolean>;

abstract class LiveGuardBase {
    private readonly actions: Map<RouteTag, ActivationCheck>;

    constructor(
        private liveService: LiveEventListService,
        private redirectHelper: RedirectHelperService,
        private history: LiveHistoryService,
    ) {
        this.actions = new Map<RouteTag, ActivationCheck>([
            [RouteTag.LiveHighlights, this.shouldRedirectToHighlights.bind(this)],
            [RouteTag.LiveAll, this.shouldRedirectToAllSports.bind(this)],
            [RouteTag.LiveSport, this.shouldRedirectToSport.bind(this)],
            [RouteTag.LiveRegion, this.shouldRedirectToRegion.bind(this)],
            [RouteTag.LiveVideo, this.shouldRedirectToVideo.bind(this)],
            [RouteTag.LiveEsports, this.shouldRedirectToEsports.bind(this)],
        ]);
    }

    protected shouldRedirect(historyFilter?: (entry: RouteHistoryData) => boolean): Observable<boolean> {
        const lastMatch = this.getLastRoute(historyFilter);
        if (lastMatch && this.actions.has(lastMatch.tag)) {
            const action = this.actions.get(lastMatch.tag)!;

            return action(lastMatch);
        }

        return of(false);
    }

    protected redirectToDefault(): Observable<boolean> {
        this.redirectHelper.goToLive();

        return of(true);
    }

    private getLastRoute(historyFilter?: (entry: RouteHistoryData) => boolean): RouteHistoryData | undefined {
        const history = this.history.getHistory();

        return findLast(history, (historyEntry) => this.actions.has(historyEntry.tag) && (historyFilter ? historyFilter(historyEntry) : true));
    }

    private shouldRedirectToHighlights(): Observable<boolean> {
        return this.redirectToDefault();
    }

    private shouldRedirectToAllSports(): Observable<boolean> {
        this.redirectHelper.goToLive(LiveUrlParam.SportList);

        return of(true);
    }

    private shouldRedirectToEsports(): Observable<boolean> {
        this.redirectHelper.goToLive(LiveUrlParam.Esports);

        return of(true);
    }

    private shouldRedirectToSport(routeData: RouteHistoryData): Observable<boolean> {
        const sportId = parseInt(routeData.params.sport);

        return this.liveService.getSportCounts(sportId).pipe(
            map((countsModel) => {
                const redirecting = this.isLive(countsModel.sport);
                if (redirecting) {
                    this.redirectHelper.goToLiveSport({ sport: countsModel.sport!, ignoreBrowserHistory: true });
                }

                return redirecting;
            }),
        );
    }

    private shouldRedirectToRegion(routeData: RouteHistoryData): Observable<boolean> {
        const sportId = parseInt(routeData.params.sport);
        const regionId = parseInt(routeData.params.region) || undefined;

        if (regionId === -1) {
            return of(false);
        }

        return this.liveService.getSportCounts(sportId, true).pipe(
            map((overview) => {
                const sport = overview.sport;
                const region = overview.regions.find((regionCountItem) => regionCountItem.id === regionId);

                const redirecting = this.isLive(sport) && this.isLive(region);

                if (sport && region && redirecting) {
                    this.redirectHelper.goToLiveSport({ sport, region, ignoreBrowserHistory: true });
                }

                return redirecting;
            }),
        );
    }

    private isLive(item: CountItem | undefined): item is CountItem {
        return !!item && item.counts && !!item.counts.live;
    }

    private shouldRedirectToVideo(): Observable<boolean> {
        return this.liveService.getLiveSportsList().pipe(
            map((sports) => {
                const isRedirecting = sports.videoEventCount > 0;

                if (isRedirecting) {
                    this.redirectHelper.goToLive(LiveUrlParam.Video);
                }

                return isRedirecting;
            }),
        );
    }
}

@Injectable()
export class LiveRootGuard extends LiveGuardBase {
    constructor(liveService: LiveEventListService, redirectHelper: RedirectHelperService, history: LiveHistoryService) {
        super(liveService, redirectHelper, history);
    }

    canActivate(): Observable<boolean> {
        return this.shouldRedirect().pipe(
            map((redirect) => {
                if (!redirect) {
                    this.redirectToDefault();
                }

                return false;
            }),
        );
    }
}

@Injectable()
export class LiveSportGuard extends LiveGuardBase {
    constructor(liveService: LiveEventListService, redirectHelper: RedirectHelperService, history: LiveHistoryService) {
        super(liveService, redirectHelper, history);
    }

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        if (toBoolean(route.queryParams.fallback) === false) {
            return of(true);
        }

        return this.shouldRedirect((routeEntry) => this.matchRegion(routeEntry, route.params)).pipe(map((r) => !r));
    }

    private matchRegion(entry: RouteHistoryData, params: Params): boolean {
        return entry.tag === RouteTag.LiveRegion && entry.params.sport === params.sport;
    }
}
